import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import ApiInspector from '../../components/ApiInspector';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { usePermesso } from '../../context/PermessiContext';
import { API_DOCS } from './MatchCampiGoPage.api';

/**
 * Match campi BOP - GO.
 *
 * GO non conosce le insegne: conosce clienti e le loro filiali. Qui si tiene il
 * ponte fra le anagrafiche BOP e i codici GO, senza il quale gli ordini
 * importati non possono essere spediti.
 */

interface Insegna { id: number; codice: string; nome: string }

interface Abbinamento {
  id: number;
  codiceGo: string;
  descrizioneGo: string | null;
  origine: 'PROPOSTA' | 'CONFERMATA' | 'MANUALE';
  confidenza: string | null;
}

interface FilialeBop {
  id: number;
  nome: string;
  citta: string | null;
  cap: string | null;
  mappatura: Abbinamento | null;
}

interface ClienteBop {
  id: number;
  codice: string;
  ragioneSociale: string | null;
  partitaIva: string | null;
  mappatura: Abbinamento | null;
  filiali: FilialeBop[];
}

interface Proposta {
  tipo: 'CLIENTE' | 'FILIALE';
  entitaId: number;
  etichettaBop: string;
  codiceGo: string | null;
  descrizioneGo: string | null;
  confidenza: string | null;
}

interface Configurazione {
  baseUrl: string;
  username: string;
  secretManagerRef: string;
  causaleDocumento: string;
  centroCosto: string | null;
  attivo: boolean;
  note: string | null;
  // scansione cartella Google Drive
  driveFolderId: string | null;
  driveAttivo: boolean;
  driveOrari: string;
  notificaEmail: string | null;
  driveUltimaScansione: string | null;
  driveUltimoSlot: string | null;
}

const CONFIG_VUOTA: Configurazione = {
  baseUrl: '', username: '', secretManagerRef: '', causaleDocumento: 'C1S', centroCosto: null, attivo: false, note: null,
  driveFolderId: null, driveAttivo: false, driveOrari: '08:00,14:00', notificaEmail: null, driveUltimaScansione: null, driveUltimoSlot: null,
};

interface EsitoVerificaDrive {
  folderId: string;
  nome: string;
  cartellaPadre: string | null;
  driveCondiviso: boolean;
  puoScrivere: boolean;
  puoSpostare: boolean;
  fileInAttesa: number;
  file: { id: string; nome: string; dimensione: number; modificato: string }[];
}

interface EsitoScansione {
  fileTrovati: number;
  lotti: { insegna: string; caricamentoId: number | null; file: string[]; esito?: string; ordini?: number; inviati?: number; blocchi?: string[] }[];
  fileNonRiconosciuti: { nome: string; motivo: string }[];
  fileDuplicati: { nome: string; motivo: string }[];
  fileIgnorati: { nome: string; motivo: string }[];
  errori: string[];
  notificaInviata?: boolean;
  durataMs: number;
}

export default function MatchCampiGoPage() {
  const { can } = usePermesso();
  const { societaId, setSocietaId, listaSocieta, isUnica, pronta } = useFiltroSocieta();

  const [insegne, setInsegne] = useState<Insegna[]>([]);
  const [insegnaId, setInsegnaId] = useState('');
  const [clienti, setClienti] = useState<ClienteBop[]>([]);
  const [daMappare, setDaMappare] = useState(0);
  const [caricando, setCaricando] = useState(false);
  const [errore, setErrore] = useState('');
  const [messaggio, setMessaggio] = useState('');

  const [config, setConfig] = useState<Configurazione>(CONFIG_VUOTA);
  const [configAperta, setConfigAperta] = useState(false);
  const [salvandoConfig, setSalvandoConfig] = useState(false);

  const [esitoDrive, setEsitoDrive] = useState<EsitoVerificaDrive | null>(null);
  const [esitoScansione, setEsitoScansione] = useState<EsitoScansione | null>(null);

  const [proposte, setProposte] = useState<Proposta[] | null>(null);
  const [sceltePropose, setScelteProposte] = useState<Record<string, boolean>>({});
  const [inCorso, setInCorso] = useState(false);

  const canEdit = can('bop_go.mappatura', 'edit');

  useEffect(() => {
    if (!pronta || !societaId) return;
    void caricaBase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pronta, societaId]);

  useEffect(() => {
    if (!societaId || !insegnaId) { setClienti([]); return; }
    void caricaMappatura();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [societaId, insegnaId]);

  async function caricaBase() {
    try {
      // @api GET /api/bop-go/import-ordini/insegne?societaId=1 -> {"success":true,"data":[{"id":11,"codice":"BRICOCENTER","nome":"BRICOCENTER"}]}
      const res = await apiFetch<{ success: boolean; data: Insegna[] }>(`/api/bop-go/import-ordini/insegne?societaId=${societaId}`);
      setInsegne(res.data || []);
      // @api GET /api/bop-go/mappatura/configurazione?societaId=1 -> {"success":true,"data":{"baseUrl":"http://…/rest/api","username":"UTENTE@GO","secretManagerRef":"go-password","causaleDocumento":"C1S","attivo":true,"note":null}}
      const resC = await apiFetch<{ success: boolean; data: Configurazione | null }>(`/api/bop-go/mappatura/configurazione?societaId=${societaId}`);
      setConfig(resC.data || CONFIG_VUOTA);
      if (!resC.data) setConfigAperta(true);
    } catch (err: any) {
      setErrore(err.message || 'Errore nel caricamento');
    }
  }

  async function caricaMappatura() {
    setCaricando(true);
    setErrore('');
    setProposte(null);
    try {
      // @api GET /api/bop-go/mappatura?societaId=1&insegnaId=11 -> {"success":true,"data":[{"id":21,"codice":"NOM00018","ragioneSociale":"Bricocenter Italia spa","partitaIva":"…","mappatura":null,"filiali":[{"id":3,"nome":"CINISELLO","citta":"…","cap":"…","mappatura":null}]}],"daMappare":68}
      const res = await apiFetch<{ success: boolean; data: ClienteBop[]; daMappare: number }>(
        `/api/bop-go/mappatura?societaId=${societaId}&insegnaId=${insegnaId}`
      );
      setClienti(res.data || []);
      setDaMappare(res.daMappare || 0);
    } catch (err: any) {
      setErrore(err.message || 'Errore nel caricamento della mappatura');
    } finally {
      setCaricando(false);
    }
  }

  async function salvaConfig() {
    setSalvandoConfig(true);
    setErrore('');
    setMessaggio('');
    try {
      // @api PUT /api/bop-go/mappatura/configurazione {"societaId":1,"baseUrl":"…","username":"…","secretManagerRef":"…","causaleDocumento":"C1S","attivo":true} -> {"success":true,"data":{"id":1}}
      await apiFetch('/api/bop-go/mappatura/configurazione', {
        method: 'PUT',
        body: JSON.stringify({ societaId: Number(societaId), ...config }),
      });
      setMessaggio('Configurazione salvata');
    } catch (err: any) {
      setErrore(err.message || 'Errore nel salvataggio');
    } finally {
      setSalvandoConfig(false);
    }
  }

  async function verificaCartellaDrive() {
    setInCorso(true);
    setErrore('');
    setMessaggio('');
    setEsitoDrive(null);
    try {
      // @api POST /api/bop-go/mappatura/drive/verifica {"societaId":1,"driveFolderId":"…"} -> {"success":true,"data":{"folderId":"…","nome":"da_elaborare","cartellaPadre":"Import_ordini_bop_go","driveCondiviso":true,"puoScrivere":true,"fileInAttesa":3,"file":[{"id":"…","nome":"ordini.xml","dimensione":12345,"modificato":"…"}]}}
      const res = await apiFetch<{ success: boolean; data: EsitoVerificaDrive }>(
        '/api/bop-go/mappatura/drive/verifica',
        { method: 'POST', body: JSON.stringify({ societaId: Number(societaId), driveFolderId: config.driveFolderId || '' }) }
      );
      setEsitoDrive(res.data);
      setMessaggio(`Cartella "${res.data.nome}"${res.data.cartellaPadre ? ` in ${res.data.cartellaPadre}` : ''} raggiungibile: ${res.data.fileInAttesa} file in attesa`
        + (!res.data.puoScrivere
          ? ' — ATTENZIONE: il backend può solo leggere: serve il ruolo Gestore contenuti sul Drive condiviso'
          : !res.data.puoSpostare
            ? ' — ATTENZIONE: il backend può leggere e creare ma NON spostare i file: cambiare il ruolo del service account in Gestore contenuti'
            : ''));
    } catch (err: any) {
      setErrore(err.message || 'Cartella non raggiungibile');
    } finally {
      setInCorso(false);
    }
  }

  async function scansionaOra() {
    if (!window.confirm('Leggere adesso la cartella Drive? I file trovati verranno caricati, elaborati e, se completi, inviati a GO.')) return;
    setInCorso(true);
    setErrore('');
    setMessaggio('');
    setEsitoScansione(null);
    try {
      // @api POST /api/bop-go/import-ordini/drive/scansiona {"societaId":1} -> {"success":true,"data":{"fileTrovati":3,"lotti":[{"insegna":"Bricocenter","caricamentoId":12,"file":["ordini.xml (tracciato bricocenter-xml)"],"esito":"inviati a GO 20 ordini su 20","ordini":20,"inviati":20}],"fileNonRiconosciuti":[],"fileDuplicati":[],"fileIgnorati":[],"errori":[],"notificaInviata":true,"durataMs":45000}}
      const res = await apiFetch<{ success: boolean; data: EsitoScansione }>(
        '/api/bop-go/import-ordini/drive/scansiona',
        { method: 'POST', body: JSON.stringify({ societaId: Number(societaId) }) }
      );
      setEsitoScansione(res.data);
      setMessaggio(`Scansione completata in ${Math.round(res.data.durataMs / 1000)} s: ${res.data.fileTrovati} file, ${res.data.lotti.length} lotti`);
      await caricaBase();
    } catch (err: any) {
      setErrore(err.message || 'Scansione fallita');
    } finally {
      setInCorso(false);
    }
  }

  async function verificaConnessione() {
    setInCorso(true);
    setErrore('');
    setMessaggio('');
    try {
      // @api POST /api/bop-go/mappatura/verifica-connessione {"societaId":1} -> {"success":true,"data":{"ok":true,"baseUrl":"…","username":"UTENTE@GO","durataMs":195}}
      const res = await apiFetch<{ success: boolean; data: { username: string; durataMs: number } }>(
        '/api/bop-go/mappatura/verifica-connessione',
        { method: 'POST', body: JSON.stringify({ societaId: Number(societaId) }) }
      );
      setMessaggio(`Connessione riuscita come ${res.data.username} in ${res.data.durataMs} ms`);
    } catch (err: any) {
      setErrore(err.message || 'Connessione fallita');
    } finally {
      setInCorso(false);
    }
  }

  async function chiediProposte() {
    setInCorso(true);
    setErrore('');
    setMessaggio('');
    try {
      // @api POST /api/bop-go/mappatura/proponi {"societaId":1,"insegnaId":11} -> {"success":true,"data":{"proposteClienti":[],"proposteFiliali":[],"totaliGo":{"clienti":1879,"filiali":604},"durataMs":3200}}
      const res = await apiFetch<{
        success: boolean;
        data: { proposteClienti: Proposta[]; proposteFiliali: Proposta[]; totaliGo: { clienti: number; filiali: number }; durataMs: number };
      }>('/api/bop-go/mappatura/proponi', {
        method: 'POST',
        body: JSON.stringify({ societaId: Number(societaId), insegnaId: Number(insegnaId) }),
      });
      const tutte = [...res.data.proposteClienti, ...res.data.proposteFiliali];
      setProposte(tutte);
      // di default si accettano solo quelle con un candidato trovato
      const scelte: Record<string, boolean> = {};
      for (const p of tutte) if (p.codiceGo) scelte[`${p.tipo}|${p.entitaId}`] = true;
      setScelteProposte(scelte);
      setMessaggio(
        `Letti da GO ${res.data.totaliGo.clienti} clienti e ${res.data.totaliGo.filiali} filiali in ${res.data.durataMs} ms — ` +
        `${tutte.filter(p => p.codiceGo).length} abbinamenti proposti su ${tutte.length} da mappare`
      );
    } catch (err: any) {
      setErrore(err.message || 'Impossibile leggere le anagrafiche da GO');
    } finally {
      setInCorso(false);
    }
  }

  async function confermaProposte() {
    if (!proposte) return;
    const voci = proposte.filter(p => p.codiceGo && sceltePropose[`${p.tipo}|${p.entitaId}`]);
    if (voci.length === 0) { setErrore('Nessun abbinamento selezionato'); return; }
    setInCorso(true);
    setErrore('');
    try {
      // @api POST /api/bop-go/mappatura/conferma {"societaId":1,"voci":[{"tipo":"FILIALE","entitaId":3,"codiceGo":"00000008","descrizioneGo":"BRICOCENTER CINISELLO BALSAMO 32","confidenza":"numero punto vendita 32"}]} -> {"success":true,"data":{"salvate":1}}
      const res = await apiFetch<{ success: boolean; data: { salvate: number } }>(
        '/api/bop-go/mappatura/conferma',
        { method: 'POST', body: JSON.stringify({ societaId: Number(societaId), voci }) }
      );
      setMessaggio(`${res.data.salvate} abbinamenti confermati`);
      setProposte(null);
      await caricaMappatura();
    } catch (err: any) {
      setErrore(err.message || 'Errore nella conferma');
    } finally {
      setInCorso(false);
    }
  }

  async function elimina(m: Abbinamento) {
    if (!window.confirm(`Rimuovere l'abbinamento con ${m.codiceGo}?`)) return;
    try {
      // @api DELETE /api/bop-go/mappatura/:id -> {"success":true}
      await apiFetch(`/api/bop-go/mappatura/${m.id}`, { method: 'DELETE' });
      await caricaMappatura();
    } catch (err: any) {
      setErrore(err.message || "Errore nell'eliminazione");
    }
  }

  function badgeAbbinamento(m: Abbinamento | null) {
    if (!m) return <span className="bop-badge bop-badge-warning">da mappare</span>;
    return (
      <>
        <span className="bop-badge bop-badge-success">{m.codiceGo}</span>{' '}
        <span className="bop-text-muted">{m.descrizioneGo || ''}</span>
      </>
    );
  }

  return (
    <Layout title="Match campi BOP-GO Smartix">
      <div className="bop-page-header">
        <h1 className="bop-page-title">Match campi BOP-GO Smartix</h1>
      </div>

      <p className="bop-text-muted">
        GO non conosce le insegne: conosce i clienti e le loro filiali. Qui si tiene il ponte fra le
        anagrafiche BOP e i codici del gestionale, senza il quale gli ordini importati non possono
        essere spediti.
      </p>

      {/* ── Configurazione ─────────────────────────────────────────── */}
      <div className="bop-card">
        <div className="bop-card__header">
          <h3 className="bop-card__title">
            Connessione a GO{' '}
            {config.attivo
              ? <span className="bop-badge bop-badge-success">attiva</span>
              : <span className="bop-badge bop-badge-neutral">non attiva</span>}
          </h3>
          <button className="bop-btn bop-btn-ghost bop-btn-sm" onClick={() => setConfigAperta(!configAperta)}>
            {configAperta ? 'Nascondi' : 'Configura'}
          </button>
        </div>
        {configAperta && (
          <div className="bop-card__body">
            <div className="bop-form-row">
              <div className="bop-form-group">
                <label className="bop-label" htmlFor="go-url">Indirizzo API *</label>
                <input id="go-url" className="bop-input" value={config.baseUrl}
                  onChange={(e) => setConfig({ ...config, baseUrl: e.target.value })}
                  placeholder="http://gocloud.gruppobonacina.it:8085/rest/api" disabled={!canEdit} />
              </div>
              <div className="bop-form-group">
                <label className="bop-label" htmlFor="go-user">Utente *</label>
                <input id="go-user" className="bop-input" value={config.username}
                  onChange={(e) => setConfig({ ...config, username: e.target.value })}
                  placeholder="UTENTE@GO" disabled={!canEdit} />
              </div>
            </div>
            <div className="bop-form-row">
              <div className="bop-form-group">
                <label className="bop-label" htmlFor="go-secret">Nome del secret con la password *</label>
                <input id="go-secret" className="bop-input" value={config.secretManagerRef}
                  onChange={(e) => setConfig({ ...config, secretManagerRef: e.target.value })}
                  placeholder="go-password oppure local://go" disabled={!canEdit} />
                <p className="bop-text-muted">
                  Il <strong>nome</strong> del secret su GCP Secret Manager, non la password.
                  In sviluppo usare <code>local://go</code> e mettere il valore in <code>GO_PASSWORD</code>.
                </p>
              </div>
              <div className="bop-form-group">
                <label className="bop-label" htmlFor="go-causale">Causale documento</label>
                <input id="go-causale" className="bop-input" value={config.causaleDocumento}
                  onChange={(e) => setConfig({ ...config, causaleDocumento: e.target.value })}
                  disabled={!canEdit} />
              </div>
              <div className="bop-form-group">
                <label className="bop-label" htmlFor="go-centro-costo">Centro di costo (contabilità analitica GO)</label>
                <input id="go-centro-costo" className="bop-input" value={config.centroCosto || ''}
                  onChange={(e) => setConfig({ ...config, centroCosto: e.target.value })}
                  placeholder="es. SM0050" disabled={!canEdit} />
                <p className="bop-text-muted">
                  Codice della tabella CEN di GO, scritto su testata e righe di ogni ordine inviato. Vuoto = nessun centro.
                </p>
              </div>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">
                <input type="checkbox" checked={config.attivo}
                  onChange={(e) => setConfig({ ...config, attivo: e.target.checked })} disabled={!canEdit} />
                {' '}Integrazione attiva
              </label>
            </div>

            <h4 className="bop-card__title">Scansione cartella Google Drive</h4>
            <p className="bop-text-muted">
              I colleghi depositano i file degli ordini (XML, TXT, CSV, XLS, PDF) nella cartella indicata, tutti insieme e senza sottocartelle.
              Agli orari impostati BOP li legge, li raggruppa per insegna riconoscendo il contenuto, crea un caricamento per lotto, elabora e —
              se ogni ordine del lotto è completo — invia a GO. I file finiscono in <code>elaborati/&lt;n. caricamento&gt;_&lt;data&gt;_&lt;ora&gt;_&lt;insegna&gt;/</code>
              accanto alla cartella di ingresso; quelli non riconosciuti in <code>errori/</code>. A ogni scansione parte una mail con l'esito.
              Il service account del backend deve essere membro del Drive condiviso con ruolo <strong>Gestore contenuti</strong> (con Collaboratore legge ma non può spostare i file).
            </p>
            <div className="bop-form-row">
              <div className="bop-form-group">
                <label className="bop-label" htmlFor="go-drive-folder">Cartella "da elaborare" (link o ID)</label>
                <input id="go-drive-folder" className="bop-input" value={config.driveFolderId || ''}
                  onChange={(e) => setConfig({ ...config, driveFolderId: e.target.value })}
                  placeholder="https://drive.google.com/drive/folders/…" disabled={!canEdit} />
              </div>
              <div className="bop-form-group">
                <label className="bop-label" htmlFor="go-drive-email">E-mail di notifica (una o più, separate da virgola)</label>
                <input id="go-drive-email" className="bop-input" value={config.notificaEmail || ''}
                  onChange={(e) => setConfig({ ...config, notificaEmail: e.target.value })}
                  placeholder="smartix@…, ordini@…" disabled={!canEdit} />
              </div>
            </div>
            <div className="bop-form-row">
              <div className="bop-form-group">
                <label className="bop-label" htmlFor="go-drive-orari">Orari di scansione (ora italiana)</label>
                <input id="go-drive-orari" className="bop-input" value={config.driveOrari}
                  onChange={(e) => setConfig({ ...config, driveOrari: e.target.value })}
                  placeholder="08:00,14:00" disabled={!canEdit} />
                <p className="bop-text-muted">Il controllo passa ogni mezz'ora: ogni orario viene eseguito una volta al giorno, alla prima occasione utile.</p>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">
                  <input type="checkbox" checked={config.driveAttivo}
                    onChange={(e) => setConfig({ ...config, driveAttivo: e.target.checked })} disabled={!canEdit} />
                  {' '}Scansione automatica attiva
                </label>
                {config.driveUltimaScansione && (
                  <p className="bop-text-muted">
                    Ultima scansione: {new Date(config.driveUltimaScansione).toLocaleString('it-IT')}
                    {config.driveUltimoSlot && ` (slot ${config.driveUltimoSlot})`}
                  </p>
                )}
              </div>
            </div>

            {canEdit && (
              <div className="bop-flex">
                <button className="bop-btn bop-btn-primary" onClick={salvaConfig} disabled={salvandoConfig}>
                  {salvandoConfig ? 'Salvataggio...' : 'Salva configurazione'}
                </button>
                <button className="bop-btn bop-btn-secondary" onClick={verificaConnessione} disabled={inCorso}>
                  Verifica connessione
                </button>
                <button className="bop-btn bop-btn-secondary" onClick={verificaCartellaDrive} disabled={inCorso || !config.driveFolderId}>
                  Verifica cartella Drive
                </button>
                <button className="bop-btn bop-btn-secondary" onClick={scansionaOra} disabled={inCorso || !config.driveFolderId}
                  title="Legge subito la cartella, senza aspettare l'orario">
                  {inCorso ? 'In corso…' : 'Scansiona ora'}
                </button>
              </div>
            )}

            {esitoDrive && esitoDrive.file.length > 0 && (
              <table className="bop-table">
                <thead><tr><th>File in attesa</th><th className="bop-text-right">Dimensione</th><th>Modificato</th></tr></thead>
                <tbody>
                  {esitoDrive.file.map(f => (
                    <tr key={f.id}>
                      <td>{f.nome}</td>
                      <td className="bop-text-right">{Math.round(f.dimensione / 1024)} KB</td>
                      <td>{new Date(f.modificato).toLocaleString('it-IT')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {esitoScansione && (
              <div className="bop-card">
                <div className="bop-card__header"><h4 className="bop-card__title">Esito scansione</h4></div>
                <div className="bop-card__body">
                  {esitoScansione.lotti.length === 0 && <p className="bop-text-muted">Nessun lotto creato.</p>}
                  {esitoScansione.lotti.map((l, i) => (
                    <p key={i}>
                      <strong>{l.insegna}</strong>
                      {l.caricamentoId && <> — <a href={`/bop-go/import-ordini-smartix/${l.caricamentoId}`}>caricamento #{l.caricamentoId}</a></>}
                      {l.ordini !== undefined && ` — ${l.ordini} ordini`}
                      {l.esito && <> — {l.blocchi && l.blocchi.length ? <span className="bop-text-danger">{l.esito}</span> : l.esito}</>}
                    </p>
                  ))}
                  {esitoScansione.fileNonRiconosciuti.map((f, i) => <p key={`n${i}`} className="bop-text-danger">Non riconosciuto: {f.nome} — {f.motivo}</p>)}
                  {esitoScansione.fileDuplicati.map((f, i) => <p key={`d${i}`} className="bop-text-muted">Già caricato: {f.nome} — {f.motivo}</p>)}
                  {esitoScansione.fileIgnorati.map((f, i) => <p key={`i${i}`} className="bop-text-muted">Ignorato: {f.nome} — {f.motivo}</p>)}
                  {esitoScansione.errori.map((e, i) => <p key={`e${i}`} className="bop-text-danger">{e}</p>)}
                  {esitoScansione.notificaInviata === false && <p className="bop-text-danger">E-mail di notifica non inviata.</p>}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Filtri ─────────────────────────────────────────────────── */}
      <div className="bop-filter-bar">
        {!isUnica && (
          <select className="bop-select" value={societaId} onChange={(e) => setSocietaId(e.target.value)}>
            <option value="">Seleziona società</option>
            {listaSocieta.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        )}
        <select className="bop-select" value={insegnaId} onChange={(e) => setInsegnaId(e.target.value)}>
          <option value="">— seleziona l'insegna —</option>
          {insegne.map(i => <option key={i.id} value={i.id}>{i.nome}</option>)}
        </select>
        {canEdit && insegnaId && (
          <button className="bop-btn bop-btn-primary" onClick={chiediProposte} disabled={inCorso}>
            {inCorso ? 'Lettura da GO...' : 'Proponi abbinamenti da GO'}
          </button>
        )}
      </div>

      {errore && <p className="bop-text-danger">{errore}</p>}
      {messaggio && <p className="bop-text-muted">{messaggio}</p>}

      {/* ── Proposte ───────────────────────────────────────────────── */}
      {proposte && (
        <div className="bop-card">
          <div className="bop-card__header">
            <h3 className="bop-card__title">Abbinamenti proposti</h3>
          </div>
          <div className="bop-card__body">
            <p className="bop-text-muted">
              Gli abbinamenti sono dedotti dal numero del punto vendita in coda alla descrizione GO e
              dalla partita IVA. Controllali prima di confermare: uno sbagliato manderebbe merce al
              negozio sbagliato senza che l'ordine sembri anomalo.
            </p>
            <table className="bop-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Tipo</th>
                  <th>In BOP</th>
                  <th>In GO</th>
                  <th>Dedotto da</th>
                </tr>
              </thead>
              <tbody>
                {proposte.map(p => {
                  const chiave = `${p.tipo}|${p.entitaId}`;
                  return (
                    <tr key={chiave}>
                      <td>
                        <input type="checkbox" disabled={!p.codiceGo}
                          checked={!!sceltePropose[chiave]}
                          onChange={(e) => setScelteProposte({ ...sceltePropose, [chiave]: e.target.checked })} />
                      </td>
                      <td>{p.tipo === 'CLIENTE' ? 'Cliente' : 'Filiale'}</td>
                      <td>{p.etichettaBop}</td>
                      <td>
                        {p.codiceGo
                          ? <><span className="bop-badge bop-badge-info">{p.codiceGo}</span> {p.descrizioneGo}</>
                          : <span className="bop-badge bop-badge-warning">nessun candidato</span>}
                      </td>
                      <td className="bop-text-muted">{p.confidenza || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="bop-flex">
              <button className="bop-btn bop-btn-primary" onClick={confermaProposte} disabled={inCorso}>
                Conferma selezionati
              </button>
              <button className="bop-btn bop-btn-secondary" onClick={() => setProposte(null)} disabled={inCorso}>
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Stato attuale ──────────────────────────────────────────── */}
      {insegnaId && !proposte && (
        <div className="bop-card">
          <div className="bop-card__header">
            <h3 className="bop-card__title">
              Anagrafiche dell'insegna
              {daMappare > 0
                ? <span className="bop-badge bop-badge-warning">{daMappare} da mappare</span>
                : <span className="bop-badge bop-badge-success">tutte mappate</span>}
            </h3>
          </div>
          <div className="bop-card__body">
            {caricando ? (
              <div className="bop-empty-state">Caricamento...</div>
            ) : clienti.length === 0 ? (
              <div className="bop-empty-state">Nessun cliente associato a questa insegna in BOP</div>
            ) : (
              clienti.map(c => (
                <div key={c.id} className="bop-card">
                  <div className="bop-card__header">
                    <h4 className="bop-card__title">
                      {c.ragioneSociale || c.codice} {badgeAbbinamento(c.mappatura)}
                    </h4>
                    {canEdit && c.mappatura && (
                      <button className="bop-btn bop-btn-ghost bop-btn-sm" onClick={() => elimina(c.mappatura!)}>
                        Rimuovi
                      </button>
                    )}
                  </div>
                  <div className="bop-card__body">
                    {c.filiali.length === 0 ? (
                      <p className="bop-text-muted">Nessuna filiale in BOP per questo cliente</p>
                    ) : (
                      <table className="bop-table">
                        <thead>
                          <tr>
                            <th>Filiale BOP</th>
                            <th>Città</th>
                            <th>Filiale GO</th>
                            <th>Origine</th>
                            <th className="bop-text-right">Azioni</th>
                          </tr>
                        </thead>
                        <tbody>
                          {c.filiali.map(f => (
                            <tr key={f.id}>
                              <td>{f.nome}</td>
                              <td className="bop-text-muted">{f.citta || '—'}</td>
                              <td>{badgeAbbinamento(f.mappatura)}</td>
                              <td className="bop-text-muted">{f.mappatura?.confidenza || '—'}</td>
                              <td className="bop-text-right">
                                {canEdit && f.mappatura && (
                                  <button className="bop-btn bop-btn-ghost bop-btn-sm" onClick={() => elimina(f.mappatura!)}>
                                    Rimuovi
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
